// =============================================================================
// Validation & Sanitization - Input Security
// =============================================================================
// PURPOSE: Provides validators (format checks) and sanitizers (data cleaners) for
// user input at system boundaries (forms, API calls, webhooks).
//
// ARCHITECTURE:
// - validators: return boolean (pass/fail) for format/constraint checks
// - sanitizers: transform strings to remove dangerous content (HTML, control chars)
//
// KEY DECISIONS:
// - Separation of concerns: validate first, then sanitize (two-step process)
// - Control char removal: preserves \t \n \r but removes potentially dangerous chars
// - DOMPurify warning: this is basic sanitization, use DOMPurify for HTML rendering
// - Max length enforcement: prevents DOS attacks via huge input strings
// =============================================================================

/**
 * Input validation and sanitization utilities
 *
 * Usage:
 * - validators: Boolean checks for data format/constraints
 * - sanitizers: Transform/clean input strings
 */

// ============================================================================
// VALIDATORS
// ============================================================================

/**
 * Validates email format using standard regex pattern
 */
function email(value: string): boolean {
  if (!value || typeof value !== "string") return false;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value.trim());
}

/**
 * Validates phone format
 * Allows: +, digits, spaces, dashes, parens
 * Minimum 10 characters after removing non-digits
 */
function phone(value: string): boolean {
  if (!value || typeof value !== "string") return false;

  // Remove all non-digit characters for length check
  const digitsOnly = value.replace(/\D/g, "");

  // Must have at least 10 digits
  if (digitsOnly.length < 10) return false;

  // Allow only valid phone characters: digits, +, -, spaces, parens
  const phoneRegex = /^[\d\s\-+()]+$/;
  return phoneRegex.test(value);
}

/**
 * Validates URL format using native URL constructor
 */
function url(value: string): boolean {
  if (!value || typeof value !== "string") return false;

  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validates UUID format (v4 pattern)
 */
function uuid(value: string): boolean {
  if (!value || typeof value !== "string") return false;

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

/**
 * Validates string length does not exceed maximum
 */
function maxLength(value: string, max: number): boolean {
  if (typeof value !== "string" || typeof max !== "number") return false;
  return value.length <= max;
}

/**
 * Validates string length meets minimum requirement
 */
function minLength(value: string, min: number): boolean {
  if (typeof value !== "string" || typeof min !== "number") return false;
  return value.length >= min;
}

/**
 * Validates number is positive (> 0) and finite
 */
function positiveNumber(value: number): boolean {
  if (typeof value !== "number") return false;
  return Number.isFinite(value) && value > 0;
}

/**
 * Validates number is within specified range (inclusive)
 */
function numberRange(value: number, min: number, max: number): boolean {
  if (typeof value !== "number" || typeof min !== "number" || typeof max !== "number") {
    return false;
  }

  if (!Number.isFinite(value)) return false;

  return value >= min && value <= max;
}

export const validators = {
  email,
  phone,
  url,
  uuid,
  maxLength,
  minLength,
  positiveNumber,
  numberRange,
};

// ============================================================================
// SANITIZERS
// ============================================================================

/**
 * Removes HTML tags from string
 * WARNING: This is basic sanitization. For user-generated content that will be
 * rendered as HTML, use a proper library like DOMPurify
 */
function stripHtml(value: string): string {
  if (typeof value !== "string") return "";

  // Remove HTML tags
  return value.replace(/<[^>]*>/g, "");
}

/**
 * Normalizes whitespace:
 * - Collapses horizontal whitespace (spaces/tabs) to a single space
 * - PRESERVES newlines (line breaks survive); caps blank-line runs at one
 * - Trims leading/trailing whitespace
 */
function normalizeWhitespace(value: string): string {
  if (typeof value !== "string") return "";

  return value
    .replace(/\r\n?/g, "\n") // Normalize CRLF / lone CR to LF
    .replace(/[^\S\n]+/g, " ") // Collapse horizontal whitespace (spaces/tabs) — preserves \n
    .replace(/[^\S\n]*\n[^\S\n]*/g, "\n") // Strip horizontal whitespace hugging each newline
    .replace(/\n{3,}/g, "\n\n") // Cap consecutive blank lines at one
    .trim(); // Strip leading/trailing whitespace
}

/**
 * Removes control characters (ASCII 0-31 except tab, newline, carriage return)
 * Preserves common whitespace while removing potentially problematic characters
 */
function removeControlChars(value: string): string {
  if (typeof value !== "string") return "";

  // Remove control characters except \t (9), \n (10), \r (13)
  // This regex intentionally matches control characters - that's its purpose
  // Matches: NUL-BS (0-8), VT (11), FF (12), SO-US (14-31), DEL (127)
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");
}

/**
 * Comprehensive string sanitization:
 * - Removes HTML tags
 * - Removes control characters
 * - Normalizes whitespace
 * - Optionally truncates to max length
 */
function sanitizeString(value: string, maxLength?: number): string {
  if (typeof value !== "string") return "";

  let sanitized = value;

  // Apply all sanitization steps
  sanitized = stripHtml(sanitized);
  sanitized = removeControlChars(sanitized);
  sanitized = normalizeWhitespace(sanitized);

  // Optionally truncate
  if (maxLength && typeof maxLength === "number" && maxLength > 0) {
    sanitized = sanitized.slice(0, maxLength);
  }

  return sanitized;
}

export const sanitizers = {
  stripHtml,
  normalizeWhitespace,
  removeControlChars,
  sanitizeString,
};
